import React, { useState } from "react";
import api from "../../api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { FaUserPlus, FaTrashAlt, FaSave, FaEuroSign, FaMapMarkerAlt, FaTruck, FaCalendarAlt } from "react-icons/fa";

const MissionForm = ({ onCreated }) => {
  const { t } = useTranslation();

  const [type, setType] = useState("");
  const [client, setClient] = useState("");
  const [address, setAddress] = useState("");
  const [departureZip, setDepartureZip] = useState("");
  const [arrivalZip, setArrivalZip] = useState("");
  const [date, setDate] = useState("");
  const [priceHT, setPriceHT] = useState("");
  const [charges, setCharges] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  const priceTTC = priceHT ? (parseFloat(priceHT) * 1.2).toFixed(2) : "0.00";
  const totalSalary = employees.reduce((sum, emp) => sum + parseFloat(emp.salary || 0), 0);

  const handleAddEmployee = () => {
    setEmployees([...employees, { employee_name: "", salary: "" }]);
  };

  const handleRemoveEmployee = (index) => {
    const updated = [...employees];
    updated.splice(index, 1);
    setEmployees(updated);
  };

  const handleEmployeeChange = (index, field, value) => {
    const updated = [...employees];
    updated[index][field] = value;
    setEmployees(updated);
  };

  const handleHTChange = (e) => setPriceHT(e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Déménagement : code postal d'arrivée obligatoire.
    if (type === "Moving" && !/^\d{5}$/.test(String(arrivalZip).trim())) {
      toast.error(t("mission.arrivalZipRequired", "Code postal d'arrivée requis pour un déménagement."));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        type,
        client,
        address: address ?? "",
        departure_zip: String(departureZip).trim(),
        arrival_zip: String(arrivalZip).trim(),
        date,
        priceHT: priceHT ? parseFloat(priceHT) : 0,
        priceTTC: priceHT ? parseFloat(priceHT) * 1.2 : 0,
        charges: charges ? parseFloat(charges) : 0,
        employees: employees
          .filter(e => e.employee_name.trim())
          .map(e => ({
            employee_name: e.employee_name.trim(),
            salary: e.salary ? parseFloat(e.salary) : 0
          }))
      };
      await api.post("/missions", payload);
      toast.success(t("mission.form.success"));
      if (onCreated) onCreated();
      setType("");
      setClient("");
      setAddress("");
      setDepartureZip("");
      setArrivalZip("");
      setDate("");
      setPriceHT("");
      setCharges("");
      setEmployees([]);
    } catch (err) {
      console.error("❌ Error creating mission:", err?.response?.data || err.message);
      toast.error(t("mission.form.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-base-100 rounded-xl border border-base-200 shadow-sm max-w-3xl mx-auto mb-8">
      <div className="px-5 py-4 border-b border-base-200 flex items-center gap-3">
        <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary">
          <FaTruck size={16} />
        </span>
        <h2 className="text-sm font-semibold text-base-content/70">{t("mission.form.title")}</h2>
      </div>

      <div className="p-5">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-base-content/70 flex items-center gap-1">
                <FaTruck size={12} /> {t("mission.form.type")}
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="select select-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                required
                disabled={loading}
              >
                <option value="">{t("mission.form.selectType")}</option>
                <option value="Moving">🚚 {t("mission.form.moving")}</option>
                <option value="Delivery">📦 {t("mission.form.delivery")}</option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-base-content/70 flex items-center gap-1">
                <FaCalendarAlt size={12} /> {t("mission.form.date")}
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                required
                disabled={loading}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-base-content/70 flex items-center gap-1">
                <FaUserPlus size={12} /> {t("mission.form.client")}
              </label>
              <input
                type="text"
                value={client}
                onChange={(e) => setClient(e.target.value)}
                className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                required
                disabled={loading}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-base-content/70 flex items-center gap-1">
                <FaMapMarkerAlt size={12} /> {t("mission.form.address")}
              </label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                disabled={loading}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-base-content/70 flex items-center gap-1">
                <FaMapMarkerAlt size={12} /> {t("mission.form.departureZip", "CP départ")}
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={departureZip}
                onChange={(e) => setDepartureZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                placeholder="75015"
                className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                disabled={loading}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-base-content/70 flex items-center gap-1">
                <FaMapMarkerAlt size={12} /> {t("mission.form.arrivalZip", "CP arrivée")}
                {type === "Moving" && <span className="text-error ml-0.5">*</span>}
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={arrivalZip}
                onChange={(e) => setArrivalZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                placeholder="37000"
                className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                disabled={loading}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-base-content/70 flex items-center gap-1">
                <FaEuroSign size={12} /> {t("mission.form.priceHT")}
              </label>
              <input
                type="number"
                value={priceHT}
                onChange={handleHTChange}
                className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                required
                disabled={loading}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-base-content/70 flex items-center gap-1">
                💸 {t("mission.form.charges")}
              </label>
              <input
                type="number"
                value={charges}
                onChange={(e) => setCharges(e.target.value)}
                className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                disabled={loading}
              />
            </div>
          </div>

          {/* Résumé total */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-5 p-4 bg-base-200 rounded-xl border border-base-300">
            <div className="text-sm font-bold text-base-content flex items-center gap-2">
              🧾 {t("mission.form.priceTTC")}:
              <span className="text-primary">{priceTTC} €</span>
            </div>
            <div className="text-sm font-semibold text-base-content/70">
              {t("mission.form.totalSalary")}:
              <span className="font-bold text-base-content ml-1">{totalSalary.toFixed(2)} €</span>
            </div>
          </div>

          {/* Employees */}
          <div className="mb-5">
            <label className="block text-sm font-semibold text-base-content/70 mb-2">
              {t("mission.form.employees")}
            </label>
            <div className="space-y-2">
              {employees.map((emp, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    type="text"
                    placeholder={t("mission.form.employeeName")}
                    value={emp.employee_name}
                    onChange={(e) => handleEmployeeChange(idx, "employee_name", e.target.value)}
                    className="col-span-5 input input-bordered input-sm bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                    required
                    disabled={loading}
                  />
                  <input
                    type="number"
                    placeholder={t("mission.form.salary")}
                    value={emp.salary}
                    onChange={(e) => handleEmployeeChange(idx, "salary", e.target.value)}
                    className="col-span-5 input input-bordered input-sm bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveEmployee(idx)}
                    className="col-span-2 btn btn-ghost btn-sm text-error hover:bg-error/10"
                    title={t("mission.form.remove")}
                    disabled={loading}
                  >
                    <FaTrashAlt size={14} />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={handleAddEmployee}
              className="mt-3 btn btn-ghost btn-sm text-base-content/70 hover:bg-base-200 flex items-center gap-2"
              disabled={loading}
            >
              <FaUserPlus size={14} /> {t("mission.form.addEmployee")}
            </button>
          </div>

          <button
            type="submit"
            className="btn btn-primary w-full flex items-center justify-center gap-2"
            disabled={loading}
          >
            <FaSave size={14} /> {t("buttons.save")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default MissionForm;
